import React, { useState } from "react";
import { Container, Typography, Button, Box } from "@mui/material";

const PrivacyPolicy = () => {
  const [lang, setLang] = useState("en"); // "en" | "ms"

  const privacyPolicyEN = `
<div class="saku-privacy">
  <style>
    .saku-privacy { --fg:#1f2937; --muted:#4b5563; --accent:#0ea5e9; font-family: system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial,sans-serif; line-height: 1.6; color: var(--fg); }
    .saku-privacy header { margin: 0 auto 24px; }
    .saku-privacy main {  margin: 0 auto; }
    .saku-privacy h1 { line-height: 1.25; margin: 0 0 8px; font-size: 2rem; font-weight: bold; }
    .saku-privacy h2 { margin-top: 28px; font-size: 1.25rem; font-weight: bold; }
    .saku-privacy p, .saku-privacy li { color: var(--fg); font-size: 1rem; }
    .saku-privacy ul { padding-left: 20px; list-style: disc; }
    .saku-privacy .meta { color: var(--muted); font-size: 0.95rem; }
    .saku-privacy .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; }
    .saku-privacy a { color: var(--accent); }
    .saku-privacy footer { margin: 36px auto 0; color: var(--muted); font-size: 0.9rem; }
  </style>

  <header>
    <h1>Privacy Policy</h1>
    <p class="meta">Last updated: October 3, 2025</p>
  </header>

  <main class="card" role="main">
    <p>
      BLoyalty Sdn Bhd (“we”, “our”, or “us”) is committed to protecting your
      personal data in accordance with the Malaysian Personal Data Protection Act 2010 (“PDPA”).
      This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data
      when you use the SAKU Application, SAKUBiz Application, or our website
      <a href="https://saku.my">https://saku.my</a>.
    </p>

    <h2 id="collection">1. Collection of Personal Data</h2>
    <p>We may collect personal data directly from you, from your interactions with our services, or from third parties where relevant. Examples include:</p>
    <ul>
      <li>Name, identification details, email, phone number, date of birth;</li>
      <li>Login credentials, PINs, and account details;</li>
      <li>Transaction details such as purchases, redemptions, rewards, and merchant interactions;</li>
      <li>Device and technical information when you use our applications or website.</li>
    </ul>

    <h2 id="obligatory">2. Obligatory and Voluntary Personal Data</h2>
    <p>
      Certain personal data is <strong>obligatory</strong> for us to provide our services (e.g., name,
      email, phone number, PIN for account access). Failure to provide obligatory data may result in:
    </p>
    <ul>
      <li>Inability to register or maintain your account;</li>
      <li>Inability to earn or redeem loyalty points, vouchers, or rewards;</li>
      <li>Restricted access to our applications or services.</li>
    </ul>
    <p>
      Other personal data is <strong>voluntary</strong> (e.g., marketing preferences, feedback, demographic
      information). You may choose not to provide such data without affecting your ability to use the core services.
    </p>

    <h2 id="purposes">3. Use of Personal Data</h2>
    <p>We process personal data for purposes including:</p>
    <ul>
      <li>Managing your membership, transactions, rewards, and redemptions;</li>
      <li>Communicating with you on account matters, promotions, and updates (where you have consented);</li>
      <li>Personalising offers, campaigns, and recommendations;</li>
      <li>Complying with legal or regulatory obligations;</li>
      <li>Improving system security, fraud detection, and overall service quality.</li>
    </ul>

    <h2 id="rights">4. Consent, Correction, and Withdrawal</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access and correct</strong> your personal data held by us; and</li>
      <li><strong>Withdraw consent</strong> to the processing of your personal data (subject to legal or contractual restrictions).</li>
    </ul>
    <p>To exercise these rights, please contact our designated Data Protection Officer (see Section 8).</p>

    <h2 id="disclosure">5. Disclosure of Personal Data</h2>
    <p>We may share your personal data with:</p>
    <ul>
      <li>Merchants whose loyalty programs you choose to join;</li>
      <li>Our service providers (IT, cloud, communications, analytics, etc.);</li>
      <li>Regulators, law enforcement agencies, or other authorities as required by law.</li>
    </ul>
    <p>All disclosures will be made in compliance with the PDPA and applicable regulations.</p>

    <h2 id="transfer">6. Transfer of Personal Data in Business Transactions</h2>
    <p>
      In the event of a <strong>sale, merger, acquisition, restructuring, or transfer of ownership or control</strong>
      of the SAKU Application, SAKUBiz Application, or our business operations, BLoyalty reserves the right to transfer
      all user data, account information, content, and related records to the new owner or successor entity.
      Such transfer shall comply with applicable data protection laws and maintain the same level of privacy protection.
      By continuing to use our applications after notification of such change, you consent to this transfer.
    </p>

    <h2 id="retention">7. Retention of Personal Data</h2>
    <p>
      We retain personal data for as long as necessary to fulfil the purposes described in this Privacy Policy,
      unless a longer retention period is required by law.
    </p>

    <h2 id="contact">8. Contact Person</h2>
    <address>
      <strong>Data Protection Officer (DPO)</strong><br />
      Designation: Compliance and Finance Manager<br />
      Email: <a href="mailto:binfinitefraud@binfinite.com.my">binfinitefraud@binfinite.com.my</a><br />
      Address: Level 16, Menara Cosway (Plaza Berjaya) No. 12, Jalan Imbi 55100, Kuala Lumpur
    </address>

    <h2 id="security">9. Security of Personal Data</h2>
    <p>
      We implement appropriate technical and organisational measures to safeguard your personal data
      against loss, misuse, unauthorised access, modification, or disclosure.
    </p>

    <h2 id="changes">10. Changes to this Policy</h2>
    <p>
      We may revise this Privacy Policy from time to time. Updates will be posted on our website or notified
      through the SAKU Application. Continued use of our services after such updates constitutes your
      acceptance of the revised Privacy Policy.
    </p>
  </main>
</div>
`;

  const privacyPolicyBM = `
<div class="saku-privacy">
  <style>
    .saku-privacy { --fg:#1f2937; --muted:#4b5563; --accent:#0ea5e9; font-family: system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial,sans-serif; line-height: 1.6; color: var(--fg); }
    .saku-privacy header { margin: 0 auto 24px; }
    .saku-privacy main {  margin: 0 auto; }
    .saku-privacy h1 { line-height: 1.25; margin: 0 0 8px; font-size: 2rem; font-weight: bold; }
    .saku-privacy h2 { margin-top: 28px; font-size: 1.25rem; font-weight: bold; }
    .saku-privacy p, .saku-privacy li { color: var(--fg); font-size: 1rem; }
    .saku-privacy ul { padding-left: 20px; list-style: disc; }
    .saku-privacy .meta { color: var(--muted); font-size: 0.95rem; }
    .saku-privacy .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; }
    .saku-privacy a { color: var(--accent); }
    .saku-privacy footer { margin: 36px auto 0; color: var(--muted); font-size: 0.9rem; }
  </style>

  <header>
    <h1>Polisi Privasi</h1>
    <p class="meta">Kemas kini terakhir: 3 Oktober 2025</p>
  </header>

  <main class="card" role="main">
    <p>
      BLoyalty Sdn Bhd (“kami”, “milik kami” atau “kita”) komited untuk melindungi
      data peribadi anda selaras dengan Akta Perlindungan Data Peribadi 2010 (“PDPA”). Polisi Privasi
      ini menerangkan bagaimana kami mengumpul, menggunakan, mendedahkan dan melindungi data peribadi anda
      apabila anda menggunakan Aplikasi SAKU, Aplikasi SAKUBiz atau laman web kami di
      <a href="https://saku.my">https://saku.my</a>.
    </p>

    <h2 id="pengumpulan">1. Pengumpulan Data Peribadi</h2>
    <p>Kami boleh mengumpul data peribadi secara langsung daripada anda, melalui interaksi anda dengan perkhidmatan kami, atau daripada pihak ketiga yang berkaitan. Contoh termasuk:</p>
    <ul>
      <li>Nama, butiran pengenalan, e-mel, nombor telefon, tarikh lahir;</li>
      <li>Kata laluan, PIN dan butiran akaun;</li>
      <li>Maklumat transaksi seperti pembelian, penebusan, ganjaran dan interaksi dengan pedagang;</li>
      <li>Maklumat peranti dan teknikal apabila anda menggunakan aplikasi atau laman web kami.</li>
    </ul>

    <h2 id="wajib">2. Data Peribadi Wajib dan Sukarela</h2>
    <p>
      Sesetengah data peribadi adalah <strong>wajib</strong> untuk kami menyediakan perkhidmatan
      (contohnya: nama, e-mel, nombor telefon, PIN untuk akses akaun). Kegagalan memberikan data wajib boleh mengakibatkan:
    </p>
    <ul>
      <li>Tidak dapat mendaftar atau mengekalkan akaun anda;</li>
      <li>Tidak dapat mengumpul atau menebus mata ganjaran, baucar atau hadiah;</li>
      <li>Akses terhad kepada aplikasi atau perkhidmatan kami.</li>
    </ul>
    <p>
      Data peribadi lain adalah <strong>sukarela</strong> (contohnya: pilihan pemasaran, maklum balas, maklumat demografi).
      Anda boleh memilih untuk tidak memberikan data ini tanpa menjejaskan akses kepada perkhidmatan teras.
    </p>

    <h2 id="tujuan">3. Tujuan Pemprosesan Data Peribadi</h2>
    <p>Kami memproses data peribadi untuk tujuan berikut:</p>
    <ul>
      <li>Mengurus keahlian, transaksi, ganjaran dan penebusan anda;</li>
      <li>Berkomunikasi mengenai akaun, promosi dan kemas kini (dengan persetujuan anda);</li>
      <li>Menyediakan tawaran, kempen dan cadangan yang diperibadikan;</li>
      <li>Mematuhi keperluan undang-undang atau peraturan;</li>
      <li>Meningkatkan keselamatan sistem, pengesanan penipuan dan kualiti perkhidmatan keseluruhan.</li>
    </ul>

    <h2 id="hak">4. Persetujuan, Pembetulan dan Penarikan Balik</h2>
    <p>Anda berhak untuk:</p>
    <ul>
      <li><strong>Mengakses dan membetulkan</strong> data peribadi anda yang disimpan oleh kami; dan</li>
      <li><strong>Menarik balik persetujuan</strong> terhadap pemprosesan data peribadi anda (tertakluk kepada sekatan undang-undang atau kontrak).</li>
    </ul>
    <p>Untuk melaksanakan hak ini, sila hubungi Pegawai Perlindungan Data kami (rujuk Seksyen 8).</p>

    <h2 id="pendedahan">5. Pendedahan Data Peribadi</h2>
    <p>Kami boleh berkongsi data peribadi anda dengan:</p>
    <ul>
      <li>Pedagang yang program kesetiaan mereka anda pilih untuk sertai;</li>
      <li>Penyedia perkhidmatan kami (IT, awan, komunikasi, analitik dan lain-lain);</li>
      <li>Pihak berkuasa pengawalseliaan, agensi penguatkuasaan undang-undang, atau pihak lain sebagaimana yang dikehendaki oleh undang-undang.</li>
    </ul>
    <p>Semua pendedahan akan dibuat selaras dengan PDPA dan peraturan yang berkaitan.</p>

    <h2 id="pemindahan">6. Pemindahan Data Peribadi dalam Urus Niaga Perniagaan</h2>
    <p>
      Sekiranya berlaku <strong>penjualan, penggabungan, pengambilalihan, penstrukturan semula, atau pemindahan hak milik atau kawalan</strong>
      ke atas Aplikasi SAKU, Aplikasi SAKUBiz atau operasi perniagaan kami, BLoyalty berhak memindahkan semua data pengguna,
      maklumat akaun, kandungan dan rekod berkaitan kepada pemilik atau entiti pengganti yang baharu. Pemindahan ini akan
      mematuhi undang-undang perlindungan data yang berkenaan dan mengekalkan tahap perlindungan privasi yang sama. Dengan
      terus menggunakan aplikasi kami selepas dimaklumkan mengenai perubahan tersebut, anda dianggap bersetuju dengan pemindahan ini.
    </p>

    <h2 id="penyimpanan">7. Penyimpanan Data Peribadi</h2>
    <p>
      Kami akan menyimpan data peribadi selama mana yang perlu untuk memenuhi tujuan yang diterangkan dalam Polisi Privasi ini,
      kecuali jika tempoh penyimpanan yang lebih lama diperlukan oleh undang-undang.
    </p>

    <h2 id="hubungi">8. Pegawai Untuk Dihubungi</h2>
    <address>
      <strong>Pegawai Perlindungan Data (DPO)</strong><br />
      Jawatan: Pengurus Pematuhan<br />
      E-mel: <a href="mailto:binfinitefraud@binfinite.com.my">binfinitefraud@binfinite.com.my</a><br />
      Alamat: Level 16, Menara Cosway (Plaza Berjaya) No. 12, Jalan Imbi 55100, Kuala Lumpur
    </address>

    <h2 id="keselamatan">9. Keselamatan Data Peribadi</h2>
    <p>
      Kami melaksanakan langkah teknikal dan organisasi yang sesuai untuk melindungi data peribadi anda
      daripada kehilangan, penyalahgunaan, capaian tanpa kebenaran, pengubahsuaian atau pendedahan.
    </p>

    <h2 id="perubahan">10. Perubahan kepada Polisi Ini</h2>
    <p>
      Kami boleh menyemak semula Polisi Privasi ini dari semasa ke semasa. Sebarang kemas kini akan dipaparkan
      di laman web kami atau dimaklumkan melalui Aplikasi SAKU. Penggunaan berterusan terhadap perkhidmatan kami
      selepas perubahan tersebut dianggap sebagai penerimaan anda terhadap Polisi Privasi yang dikemas kini.
    </p>
  </main>
</div>
`;

  return (
    <Container sx={{ padding: "24px", marginTop: "100px", maxWidth: "800px" }}>
      {/* Language Toggle */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant={lang === "en" ? "contained" : "outlined"}
          onClick={() => setLang("en")}
          sx={{ mr: 1 }}
        >
          ENGLISH
        </Button>
        <Button
          variant={lang === "ms" ? "contained" : "outlined"}
          onClick={() => setLang("ms")}
        >
          MALAY
        </Button>
      </Box>

      {/* Content Based on Language */}
      {lang === "en" ? (
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: privacyPolicyEN,
            }}
          />
        </>
      ) : (
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: privacyPolicyBM,
            }}
          />
        </>
      )}
    </Container>
  );
};

export default PrivacyPolicy;
